import { useId } from 'react';

interface StarRatingProps {
  rating?: number;
  maxStars?: number;
  color?: string;
}

// Convert hex color to RGBA with specified alpha
const hexToRgba = (hex: string, alpha: number = 1): string => {
  const cleanHex = hex.replace(/^#/, '');
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const StarRating: React.FC<StarRatingProps> = ({
  rating = 0,
  maxStars = 5,
  color = '#FFD700',
}) => {
  const uniqueId = useId();
  const roundedRating = Number(rating.toFixed(2));
  const fullStars = Math.floor(roundedRating);
  const decimalPart = Number((roundedRating % 1).toFixed(2));
  const hasPartialStar = decimalPart > 0;
  const emptyStars = maxStars - Math.ceil(roundedRating);
  const backgroundColor = hexToRgba(color, 0.1);

  return (
    <div className="flex items-center space-x-[2px]">
      {[...Array(fullStars)].map((_, index) => (
        <svg
          key={`full-${index}`}
          className="w-4 h-[15px]"
          viewBox="0 0 16 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_4540_4094)">
            <path
              d="M8.80302 0.992188C8.6558 0.691406 8.34469 0.5 8.00302 0.5C7.66136 0.5 7.35302 0.691406 7.20302 0.992188L5.41691 4.60977L1.42802 5.18945C1.09469 5.23867 0.816911 5.46836 0.714133 5.78281C0.611355 6.09727 0.694689 6.44453 0.933577 6.67695L3.82802 9.49609L3.14469 13.4801C3.08913 13.8082 3.22802 14.1418 3.50302 14.3359C3.77802 14.5301 4.14191 14.5547 4.44191 14.3988L8.0058 12.5258L11.5697 14.3988C11.8697 14.5547 12.2336 14.5328 12.5086 14.3359C12.7836 14.1391 12.9225 13.8082 12.8669 13.4801L12.1808 9.49609L15.0752 6.67695C15.3141 6.44453 15.4002 6.09727 15.2947 5.78281C15.1891 5.46836 14.9141 5.23867 14.5808 5.18945L10.5891 4.60977L8.80302 0.992188Z"
              fill={color}
            />
          </g>
          <defs>
            <clipPath id="clip0_4540_4094">
              <rect width="16" height="14" fill="white" transform="translate(0 0.5)" />
            </clipPath>
          </defs>
        </svg>
      ))}
      {hasPartialStar && (
        <svg
          className="w-4 h-[15px]"
          viewBox="0 0 16 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id={`partial-star-${uniqueId}`}>
              <stop offset={`${decimalPart * 100}%`} stopColor={color} />
              <stop offset={`${decimalPart * 100}%`} stopColor={backgroundColor} />
            </linearGradient>
          </defs>
          <g clipPath="url(#clip0_4540_4094)">
            <path
              d="M8.80302 0.992188C8.6558 0.691406 8.34469 0.5 8.00302 0.5C7.66136 0.5 7.35302 0.691406 7.20302 0.992188L5.41691 4.60977L1.42802 5.18945C1.09469 5.23867 0.816911 5.46836 0.714133 5.78281C0.611355 6.09727 0.694689 6.44453 0.933577 6.67695L3.82802 9.49609L3.14469 13.4801C3.08913 13.8082 3.22802 14.1418 3.50302 14.3359C3.77802 14.5301 4.14191 14.5547 4.44191 14.3988L8.0058 12.5258L11.5697 14.3988C11.8697 14.5547 12.2336 14.5328 12.5086 14.3359C12.7836 14.1391 12.9225 13.8082 12.8669 13.4801L12.1808 9.49609L15.0752 6.67695C15.3141 6.44453 15.4002 6.09727 15.2947 5.78281C15.1891 5.46836 14.9141 5.23867 14.5808 5.18945L10.5891 4.60977L8.80302 0.992188Z"
              fill={`url(#partial-star-${uniqueId})`}
            />
          </g>
          <g clipPath="url(#clip0_4540_4096)">
            <path
              d="M7.99732 0.5C8.25288 0.5 8.48621 0.642187 8.59732 0.869141L10.5029 4.73281L14.7584 5.35078C15.0084 5.38633 15.2168 5.55859 15.2945 5.79648C15.3723 6.03437 15.3084 6.29141 15.1307 6.46641L12.0445 9.47969L12.7723 13.7344C12.814 13.9805 12.7112 14.2293 12.5029 14.377C12.2945 14.5246 12.0223 14.541 11.8001 14.4234L7.99454 12.4219L4.19454 14.4207C3.96954 14.5383 3.69732 14.5219 3.49177 14.3742C3.28621 14.2266 3.18065 13.9777 3.22232 13.7316L3.9501 9.47695L0.863988 6.46641C0.683432 6.29141 0.622321 6.03164 0.700099 5.79648C0.777877 5.56133 0.98621 5.38906 1.23621 5.35078L5.49177 4.73281L7.39732 0.869141C7.51121 0.642187 7.74177 0.5 7.99732 0.5ZM7.99732 2.66016L6.53899 5.61875C6.44177 5.81289 6.25565 5.94961 6.03621 5.98242L2.7501 6.4582L5.13621 8.78516C5.28899 8.93555 5.36121 9.14883 5.3251 9.35938L4.76121 12.6324L7.68343 11.0957C7.88065 10.9918 8.11677 10.9918 8.31121 11.0957L11.2334 12.6324L10.6723 9.36211C10.6362 9.15156 10.7057 8.93828 10.8612 8.78789L13.2473 6.46094L9.96121 5.98242C9.74454 5.94961 9.55565 5.81562 9.45843 5.61875L7.99732 2.66016Z"
              fill={color}
            />
          </g>
          <defs>
            <clipPath id="clip0_4540_4094">
              <rect width="16" height="14" fill="white" transform="translate(0 0.5)" />
            </clipPath>
            <clipPath id="clip0_4540_4096">
              <rect width="16" height="14" fill="white" transform="translate(0 0.5)" />
            </clipPath>
          </defs>
        </svg>
      )}
      {[...Array(emptyStars)].map((_, index) => (
        <svg
          key={`empty-${index}`}
          className="w-4 h-[15px]"
          viewBox="0 0 16 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_4540_4096)">
            <path
              d="M7.99732 0.5C8.25288 0.5 8.48621 0.642187 8.59732 0.869141L10.5029 4.73281L14.7584 5.35078C15.0084 5.38633 15.2168 5.55859 15.2945 5.79648C15.3723 6.03437 15.3084 6.29141 15.1307 6.46641L12.0445 9.47969L12.7723 13.7344C12.814 13.9805 12.7112 14.2293 12.5029 14.377C12.2945 14.5246 12.0223 14.541 11.8001 14.4234L7.99454 12.4219L4.19454 14.4207C3.96954 14.5383 3.69732 14.5219 3.49177 14.3742C3.28621 14.2266 3.18065 13.9777 3.22232 13.7316L3.9501 9.47695L0.863988 6.46641C0.683432 6.29141 0.622321 6.03164 0.700099 5.79648C0.777877 5.56133 0.98621 5.38906 1.23621 5.35078L5.49177 4.73281L7.39732 0.869141C7.51121 0.642187 7.74177 0.5 7.99732 0.5ZM7.99732 2.66016L6.53899 5.61875C6.44177 5.81289 6.25565 5.94961 6.03621 5.98242L2.7501 6.4582L5.13621 8.78516C5.28899 8.93555 5.36121 9.14883 5.3251 9.35938L4.76121 12.6324L7.68343 11.0957C7.88065 10.9918 8.11677 10.9918 8.31121 11.0957L11.2334 12.6324L10.6723 9.36211C10.6362 9.15156 10.7057 8.93828 10.8612 8.78789L13.2473 6.46094L9.96121 5.98242C9.74454 5.94961 9.55565 5.81562 9.45843 5.61875L7.99732 2.66016Z"
              fill={color}
            />
          </g>
          <defs>
            <clipPath id="clip0_4540_4096">
              <rect width="16" height="14" fill="white" transform="translate(0 0.5)" />
            </clipPath>
          </defs>
        </svg>
      ))}
      {/* <span className="ml-2 text-gray-600">{roundedRating.toFixed(2)}</span> */}
    </div>
  );
};

export default StarRating;